import { Link } from 'react-router-dom';
import { memo, useCallback } from 'react';

function Footer() {
  const scrollToSection = useCallback((sectionId: string) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  return (
    <footer className="bg-gradient-to-br from-sky-50 to-sky-100 pt-12 sm:pt-16 pb-6 sm:pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 sm:gap-10 lg:gap-8 mb-10 sm:mb-12">
          <div className="col-span-2 md:col-span-3 lg:col-span-1">
            <div className="flex items-center mb-4">
              <div className="flex flex-col">
                <span className="text-base sm:text-lg font-bold text-sky-600 leading-tight tracking-wide uppercase">DAVIS COUNTY</span>
                <span className="text-xs text-gray-600 leading-tight">Disaster Cleanup</span>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-4 max-w-xs">
              Professional water damage restoration services across Utah. Available 24/7 for emergency response.
            </p>
            <div className="flex space-x-3">
              <a
                href="https://www.facebook.com/Floodsmen-1107842726065270/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center bg-white rounded-lg text-sky-500 hover:bg-sky-500 hover:text-white transition-colors cursor-pointer"
              >
                <i className="ri-facebook-fill text-lg"></i>
              </a>
              <a
                href="http://yelp.com/biz/floodsmen-disaster-cleanup-clearfield-2"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center bg-white rounded-lg text-sky-500 hover:bg-sky-500 hover:text-white transition-colors cursor-pointer"
              >
                <i className="ri-star-smile-fill text-lg"></i>
              </a>
            </div>
          </div>

          <div>
            <h3 className="text-sm sm:text-base font-bold text-gray-900 mb-3 sm:mb-4">Quick Links</h3>
            <ul className="space-y-2 sm:space-y-2.5">
              <li>
                <Link to="/" className="text-sm text-gray-600 hover:text-sky-500 transition-colors cursor-pointer">
                  Home
                </Link>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('about')}
                  className="text-sm text-gray-600 hover:text-sky-500 transition-colors cursor-pointer"
                >
                  About Us
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('services')}
                  className="text-sm text-gray-600 hover:text-sky-500 transition-colors cursor-pointer"
                >
                  Services
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('testimonials')}
                  className="text-sm text-gray-600 hover:text-sky-500 transition-colors cursor-pointer"
                >
                  Testimonials
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('contact')}
                  className="text-sm text-gray-600 hover:text-sky-500 transition-colors cursor-pointer"
                >
                  Contact
                </button>
              </li>
              <li>
                <Link to="/blog" className="text-sm text-gray-600 hover:text-sky-500 transition-colors cursor-pointer">
                  Blog
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-sm sm:text-base font-bold text-gray-900 mb-3 sm:mb-4">Our Services</h3>
            <ul className="space-y-2 sm:space-y-2.5">
              <li>
                <Link to="/services/flood-cleanup" className="text-sm text-gray-600 hover:text-sky-500 transition-colors cursor-pointer">
                  Flood Cleanup
                </Link>
              </li>
              <li>
                <Link to="/services/sewage-cleanup" className="text-sm text-gray-600 hover:text-sky-500 transition-colors cursor-pointer">
                  Sewage Cleanup
                </Link>
              </li>
              <li>
                <Link to="/services/mold-removal" className="text-sm text-gray-600 hover:text-sky-500 transition-colors cursor-pointer">
                  Mold Removal
                </Link>
              </li>
              <li>
                <Link to="/services/odor-removal" className="text-sm text-gray-600 hover:text-sky-500 transition-colors cursor-pointer">
                  Odor Removal
                </Link>
              </li>
              <li>
                <Link to="/services/smoke-damage" className="text-sm text-gray-600 hover:text-sky-500 transition-colors cursor-pointer">
                  Smoke Damage
                </Link>
              </li>
              <li>
                <Link to="/services/storm-damage" className="text-sm text-gray-600 hover:text-sky-500 transition-colors cursor-pointer">
                  Storm Damage
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-sm sm:text-base font-bold text-gray-900 mb-3 sm:mb-4">Service Areas</h3>
            <ul className="space-y-2 sm:space-y-2.5">
              <li>
                <Link to="/bountiful" className="text-sm text-gray-600 hover:text-sky-500 transition-colors cursor-pointer">
                  Bountiful
                </Link>
              </li>
              <li>
                <Link to="/centerville" className="text-sm text-gray-600 hover:text-sky-500 transition-colors cursor-pointer">
                  Centerville
                </Link>
              </li>
              <li>
                <Link to="/clearfield" className="text-sm text-gray-600 hover:text-sky-500 transition-colors cursor-pointer">
                  Clearfield
                </Link>
              </li>
              <li>
                <Link to="/farmington" className="text-sm text-gray-600 hover:text-sky-500 transition-colors cursor-pointer">
                  Farmington
                </Link>
              </li>
              <li>
                <Link to="/fruit-heights" className="text-sm text-gray-600 hover:text-sky-500 transition-colors cursor-pointer">
                  Fruit Heights
                </Link>
              </li>
              <li>
                <Link to="/kaysville" className="text-sm text-gray-600 hover:text-sky-500 transition-colors cursor-pointer">
                  Kaysville
                </Link>
              </li>
              <li>
                <Link to="/layton" className="text-sm text-gray-600 hover:text-sky-500 transition-colors cursor-pointer">
                  Layton
                </Link>
              </li>
              <li>
                <Link to="/syracuse" className="text-sm text-gray-600 hover:text-sky-500 transition-colors cursor-pointer">
                  Syracuse
                </Link>
              </li>
            </ul>
          </div>

          <div className="col-span-2 md:col-span-3 lg:col-span-1">
            <h3 className="text-sm sm:text-base font-bold text-gray-900 mb-3 sm:mb-4">Contact Us</h3>
            <ul className="space-y-3 sm:space-y-4">
              <li className="flex items-start space-x-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-phone-line text-sky-500"></i>
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-gray-600">24/7 Emergency</p>
                  <a href="tel:3853406632" className="text-sm font-semibold text-gray-900 hover:text-sky-500 cursor-pointer">
                    (385) 340-6632
                  </a>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-mail-line text-sky-500"></i>
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-gray-600">Email Us</p>
                  <a href="mailto:info@daviscountydisastercleanup.com" className="text-sm font-semibold text-gray-900 hover:text-sky-500 cursor-pointer break-all">
                    info@daviscountydisastercleanup.com
                  </a>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-map-pin-line text-sky-500"></i>
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-gray-600">Service Area</p>
                  <p className="text-sm font-semibold text-gray-900">Davis County, Utah</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-6 sm:pt-8 border-t border-sky-200">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-xs sm:text-sm text-gray-600">
              © 2026 Clear Advantage Ai LLC. All rights reserved.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6">
              <a href="#" className="text-xs sm:text-sm text-gray-600 hover:text-sky-500 transition-colors cursor-pointer">
                Privacy Policy
              </a>
              <a href="#" className="text-xs sm:text-sm text-gray-600 hover:text-sky-500 transition-colors cursor-pointer">
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default memo(Footer);
